import { TFunction } from 'i18next';
import { AIActionBinding, createAIActionBinding } from 'models/copilot/actions/aiActionBinding';
import { processStartTemplate } from 'models/copilot/actions/aiActionTemplates';
import { getSceneSelectionQuestion } from 'models/copilot/actions/aiEntityActionTemplates';
import { CopilotAction } from 'models/copilot/copilotAction';
import { CopilotPanelContentBase, CopilotPanelContext } from 'models/copilot/copilotBridge';
import {
  CopilotPanelContextDefinition,
  CopilotPanelEntityRef,
  CopilotPanelProcessState,
  CopilotPanelRuntime,
} from 'models/copilot/copilotPanelContextDefinition';
import { CopilotPreviewContent } from 'models/copilot/copilotPreviewContent';
import { CopilotScene } from 'models/copilot/copilotScene';
import {
  CopilotProcessSuggestionsContent,
  TenantProcessStateProcessSuggestions,
} from 'models/copilot/Processes/ProcessSuggestions/copilotProcessSuggestion';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import { EntityTypes } from 'models/entity';
import { apiCopilotExecuteProcessSuggestions } from 'services/Api/copilotService';
import { getCopilotProcessWelcomeMessage } from '../copilotProcessMessages';
import { getSceneBindings, withSceneHelpFirst } from '../Generic/genericSceneBindings';
import { CopilotProcessesPanelState } from './copilotProcessesPanelState';

export class CopilotProcessesPanelContextDefinition extends CopilotPanelContextDefinition {
  constructor() {
    super();
    this.context = CopilotPanelContext.Processes;
    this.name = 'Processes';
    this.supportsChat = true;
    this.requiresActiveAction = true;
  }

  getBindings(_content: CopilotPanelContentBase | undefined): AIActionBinding[] {
    return withSceneHelpFirst([
      createAIActionBinding(processStartTemplate, { processId: TenantProcess.ProcessSuggestions }),
      ...getSceneBindings(CopilotScene.Processes),
    ]);
  }

  // the processes list's summarize prompt, worded as on the Generic list scene
  getEntitySelectionQuestion(selectedEntityIds: number[], t: TFunction<string[]>): string | undefined {
    if (selectedEntityIds.length === 0) return undefined;

    return getSceneSelectionQuestion(t, CopilotScene.Processes, selectedEntityIds.length);
  }

  async getAnswer(runtime: CopilotPanelRuntime, _t: TFunction<string[]>): Promise<void> {
    const { action } = runtime;
    if (action?.processId !== TenantProcess.ProcessSuggestions) return;
    const state = this.getState(runtime);
    const content = runtime.content as CopilotProcessSuggestionsContent | undefined;
    if (!state || !content) return;

    runtime.setIsLoading(true);
    const currentProcesses = content.currentProcesses;
    state.setCurrentProcesses(currentProcesses);

    const answer = await apiCopilotExecuteProcessSuggestions(
      action.status || TenantProcessStatus.NotStarted,
      runtime.conversation,
      action.status === TenantProcessStatus.NotStarted ? [] : state.processes,
      currentProcesses,
      runtime.connectionId,
      runtime.accessToken,
    );
    if (!answer) return;

    runtime.setConversation((prev) => runtime.mergeAdaptiveInputs(prev, answer.conversation));
    if (action.status === TenantProcessStatus.Completed) {
      runtime.resultBus.publishProcessSuggestions(answer);
    } else {
      state.setProcesses(answer.processes);
      state.setProcessesDisplay(answer.processes.map((process) => process.clone()));
      runtime.setPreviewContent(CopilotPreviewContent.Usage);
    }
  }

  getEntityRef(content: CopilotPanelContentBase | undefined): CopilotPanelEntityRef | undefined {
    if (!this.isAvailableForContent(content)) return undefined;

    return { entityType: EntityTypes.NotSet, entityId: 0 };
  }

  getWelcomeMessage(action: CopilotAction, t: TFunction<string[]>): string | undefined {
    if (action.processId !== TenantProcess.ProcessSuggestions) return undefined;

    return getCopilotProcessWelcomeMessage(action, t);
  }

  getCreatePreviewText(t: TFunction<string[]>): string | undefined {
    return t('copilot:Actions.Processes.Create');
  }

  canSaveDisplayState(runtime: CopilotPanelRuntime): boolean {
    const state = this.getState(runtime);

    return state?.processesDisplay.every((process) => process.name?.trim()) ?? true;
  }

  applyProcessState(runtime: CopilotPanelRuntime, state: CopilotPanelProcessState): CopilotPreviewContent | undefined {
    const panelState = this.getState(runtime);
    if (!(state.data instanceof TenantProcessStateProcessSuggestions) || !panelState) {
      return super.applyProcessState(runtime, state);
    }

    panelState.setProcesses(state.data.processes);
    panelState.setProcessesDisplay(state.data.processes.map((process) => process.clone()));
    panelState.setCurrentProcesses(state.data.currentProcesses);
    runtime.setConversation(state.conversation);

    return state.data.processes.length > 0 ? CopilotPreviewContent.Usage : undefined;
  }

  clearPreview(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    state?.setProcesses([]);
    state?.setProcessesDisplay([]);
  }

  saveDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setProcesses(
      state.processesDisplay.map((process) => {
        const clone = process.clone();
        clone.name = clone.name.trim();

        return clone;
      }),
    );
  }

  discardDisplayState(runtime: CopilotPanelRuntime): void {
    const state = this.getState(runtime);
    if (!state) return;

    state.setProcessesDisplay(state.processes.map((process) => process.clone()));
  }

  private getState(runtime: CopilotPanelRuntime): CopilotProcessesPanelState | undefined {
    return runtime.getUsageState<CopilotProcessesPanelState>(this.context);
  }
}
